import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Manual Carrefour (MAF MKP) fulfilment controls — fallback when automation is off.
// order.carrefour.status: new | packed | shipped | delivered | delivery_failed | cancelled
export const CarrefourActions = ({ order, onRefresh }) => {
  const [busy, setBusy] = useState('');
  const [carrier, setCarrier] = useState('');
  const [awb, setAwb] = useState('');
  const [failReason, setFailReason] = useState('');
  const st = order.carrefour?.status;

  const call = async (key, path, body, msg, patch) => {
    setBusy(key);
    try {
      const r = await axios.post(`${API_BASE_URL}/carrefour/orders/${order.id}/${path}`, body || {}, authCfg());
      toast.success(r.data?.message || msg);
      onRefresh && onRefresh({ ...order, carrefour: { ...order.carrefour, ...(patch || {}) } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-2 pt-1 border-t border-blue-500/20" data-testid="carrefour-actions">
      {st === 'new' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white flex-1" disabled={busy}
            data-testid="carrefour-pack-btn"
            onClick={() => call('pack', 'pack', {}, 'Accepted & packed on Carrefour', { status: 'packed' })}>
            {busy === 'pack' ? 'Packing…' : 'Accept & Pack'}
          </Button>
          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" disabled={busy}
            data-testid="carrefour-refuse-btn"
            onClick={() => { if (window.confirm('Refuse this Carrefour order?')) call('refuse', 'refuse', {}, 'Order refused', { status: 'cancelled' }); }}>
            {busy === 'refuse' ? 'Refusing…' : 'Refuse'}
          </Button>
        </div>
      )}

      {st === 'packed' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="carrefour-carrier-input"
              className="h-9 px-2 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-sm">
              <option value="">Default carrier</option>
              <option value="Self">Self</option>
              <option value="iMile">iMile</option>
              <option value="Aramex">Aramex</option>
            </select>
            <input type="text" placeholder="AWB (needed unless Self)" value={awb}
              onChange={(e) => setAwb(e.target.value)} data-testid="carrefour-awb-input"
              className="flex-1 min-w-[140px] h-9 px-3 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-sm" />
          </div>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white w-full" disabled={busy}
            data-testid="carrefour-ship-btn"
            onClick={() => call('ship', 'ship',
              { carrier: carrier || null, awb: awb || null },
              'Shipment reported to Carrefour', { status: 'shipped', awb: awb || order.carrefour?.awb })}>
            {busy === 'ship' ? 'Reporting…' : 'Report Shipped'}
          </Button>
        </div>
      )}

      {st === 'shipped' && (
        <div className="space-y-2">
          <p className="text-xs text-wt-text-muted">Delivery with POD is pushed automatically when your driver completes the order. If the delivery failed:</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="text" placeholder="Failure reason" value={failReason}
              onChange={(e) => setFailReason(e.target.value)} data-testid="carrefour-fail-reason-input"
              className="flex-1 min-w-[140px] h-9 px-3 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-sm" />
            <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" disabled={busy}
              data-testid="carrefour-deliver-failed-btn"
              onClick={() => call('fail', 'deliver-failed', { reason: failReason }, 'Delivery failure reported', { status: 'delivery_failed' })}>
              {busy === 'fail' ? 'Reporting…' : 'Delivery Failed'}
            </Button>
          </div>
        </div>
      )}

      {!order.shopify_order_id && (
        <Button size="sm" variant="outline" className="w-full border-wt-border text-wt-text-primary" disabled={busy}
          data-testid="carrefour-push-shopify-btn"
          onClick={async () => {
            setBusy('shopify');
            try {
              const r = await axios.post(`${API_BASE_URL}/orders/${order.id}/push-to-shopify`, {}, authCfg());
              toast.success('Created on Shopify — Carrefour number stays main');
              onRefresh && onRefresh({ ...order, shopify_order_id: r.data?.shopify_order_id });
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Shopify push failed');
            } finally {
              setBusy('');
            }
          }}>
          {busy === 'shopify' ? 'Pushing…' : 'Push to Shopify'}
        </Button>
      )}
    </div>
  );
};

export default CarrefourActions;
